'use client'

import dynamic from 'next/dynamic'
import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import type { ProductPageType } from '../../product-page'
import { useFeatureFlag } from 'packages/microsite-data/src/feature-flags/client'

const ProductStickyActionBar = dynamic(() => import('./product-sticky-action-bar'), { ssr: false })

type Props = {
  product: ProductPageType
  children: ReactNode
  actionComponent: ReactNode
}

const ProductStickyActionBarObserver = ({ product, children, actionComponent }: Props) => {
  const containerRef = useRef(null)
  const [showActionBar, setShowActionBar] = useState(false)
  const { enabled } = useFeatureFlag('ab-test-product-sticky-add-to-cart')

  useEffect(() => {
    if (!enabled) {
      return
    }

    const container = containerRef.current

    if (!container) return

    const observer = new IntersectionObserver(([entry]) => {
      setShowActionBar(!entry.isIntersecting && entry.boundingClientRect.top < 0)
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [enabled])

  return (
    <>
      {enabled && (
        <ProductStickyActionBar product={product} isVisible={showActionBar}>
          {actionComponent}
        </ProductStickyActionBar>
      )}
      <div ref={containerRef}>{children}</div>
    </>
  )
}

export default memo(ProductStickyActionBarObserver)
